import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Chip,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  Award,
  Search,
  Calendar,
  User,
  BookOpen,
  Shield,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

const CertificateVerify = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();

  const [certId, setCertId] = useState(certificateId || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Auto-verify on mount if certificateId is in URL
  useEffect(() => {
    if (certificateId) {
      verifyCertificate(certificateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId]);

  const verifyCertificate = async (id) => {
    if (!id?.trim()) return;
    setLoading(true);
    setResult(null);
    setSearched(true);
    try {
      const res = await fetch(`${API_BASE}/api/certificates/verify/${id.trim()}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.valid) {
        setResult({ valid: true, ...data });
      } else {
        setResult({ valid: false, message: data?.message || 'Certificate not found or revoked.' });
      }
    } catch {
      setResult({ valid: false, message: 'Unable to verify. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (certId.trim()) {
      navigate(`/verify/${certId.trim()}`);
      verifyCertificate(certId.trim());
    }
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#09090b' }}>
      <Header />

      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Page title */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.6,
                borderRadius: 6,
                border: '1px solid rgba(124,58,237,0.28)',
                bgcolor: 'rgba(124,58,237,0.07)',
                mb: 2.5,
              }}
            >
              <Shield size={13} color="#a78bfa" />
              <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, letterSpacing: 0.8 }}>
                CREDENTIAL VERIFICATION
              </Typography>
            </Box>
            <Typography
              variant="h4"
              sx={{ color: '#fafafa', fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', md: '2.1rem' } }}
            >
              Verify a Certificate
            </Typography>
            <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
              Enter a certificate ID to instantly verify authenticity and view credential details.
            </Typography>
          </Box>

          {/* Search form card */}
          <Paper
            elevation={0}
            component="form"
            onSubmit={handleSearch}
            sx={{
              p: 3,
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.08)',
              mb: 4,
            }}
          >
            <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 1.5, fontWeight: 500 }}>
              Certificate ID
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                placeholder="e.g. QN-CERT-2024-ABC123"
                value={certId}
                onChange={(e) => setCertId(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Award size={16} color="#52525b" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    borderRadius: 2,
                    color: '#fafafa',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                    '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                  },
                  '& input::placeholder': { color: '#52525b' },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !certId.trim()}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  bgcolor: '#7c3aed',
                  '&:hover': { bgcolor: '#6d28d9' },
                }}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: 'white' }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Search size={15} />
                    Verify
                  </Box>
                )}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#52525b', mt: 1.5, display: 'block' }}>
              Certificate IDs are found on QoreNova Academy credential documents.
            </Typography>
          </Paper>
        </motion.div>

        {/* ── Result States ── */}
        <AnimatePresence mode="wait">
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={40} sx={{ color: '#7c3aed', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#71717a' }}>
                  Verifying certificate…
                </Typography>
              </Box>
            </motion.div>
          )}

          {/* ── Valid ── */}
          {!loading && result && result.valid && (
            <motion.div
              key="valid"
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid rgba(16,185,129,0.22)',
                  background: 'rgba(255,255,255,0.025)',
                }}
              >
                {/* Green accent bar */}
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #10b981, #059669)' }} />

                {/* Valid header */}
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(16,185,129,0.05)',
                    borderBottom: '1px solid rgba(16,185,129,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      bgcolor: 'rgba(16,185,129,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle size={26} color="#10b981" />
                  </Box>
                  <Box>
                    <Chip
                      label="✓ VERIFIED AUTHENTIC"
                      sx={{
                        bgcolor: 'rgba(16,185,129,0.12)',
                        color: '#10b981',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: 0.5,
                        mb: 0.5,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#71717a', display: 'block' }}>
                      Verified by QoreNova Academy
                    </Typography>
                  </Box>
                </Box>

                {/* Certificate details */}
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {[
                      {
                        icon: <User size={15} color="#71717a" />,
                        label: 'Graduate Name',
                        value: result.studentName || result.userName || 'N/A',
                      },
                      {
                        icon: <BookOpen size={15} color="#71717a" />,
                        label: 'Course',
                        value: result.courseName || result.courseTitle || 'N/A',
                      },
                      {
                        icon: <Calendar size={15} color="#71717a" />,
                        label: 'Completion Date',
                        value: formatDate(result.completedAt || result.issuedAt),
                      },
                      {
                        icon: <Award size={15} color="#71717a" />,
                        label: 'Certificate ID',
                        value: result.certificateId || certId,
                      },
                    ].map((item, i) => (
                      <Box
                        key={i}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          {item.icon}
                          <Typography
                            variant="caption"
                            sx={{ color: '#71717a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#fafafa' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2.5 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(124,58,237,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Shield size={18} color="#a78bfa" />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#fafafa', lineHeight: 1.2 }}>
                          QoreNova Academy
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#71717a' }}>
                          Issuing Authority
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ExternalLink size={13} />}
                      onClick={() => navigate('/certifications')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        borderColor: 'rgba(255,255,255,0.1)',
                        color: '#a1a1aa',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' },
                      }}
                    >
                      All Certifications
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          )}

          {/* ── Invalid ── */}
          {!loading && result && !result.valid && (
            <motion.div
              key="invalid"
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid rgba(239,68,68,0.22)',
                  background: 'rgba(255,255,255,0.025)',
                }}
              >
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #ef4444, #dc2626)' }} />
                <Box sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(239,68,68,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <XCircle size={32} color="#ef4444" />
                  </Box>
                  <Chip
                    label="✗ NOT VERIFIED"
                    sx={{
                      bgcolor: 'rgba(239,68,68,0.1)',
                      color: '#ef4444',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      letterSpacing: 0.5,
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fafafa', mb: 1 }}>
                    Certificate not found or invalid
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
                    {result.message}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={() => { setResult(null); setCertId(''); setSearched(false); }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#7c3aed',
                        '&:hover': { bgcolor: '#6d28d9' },
                      }}
                    >
                      Try Again
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowLeft size={15} />}
                      onClick={() => navigate('/certifications')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.1)',
                        color: '#a1a1aa',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' },
                      }}
                    >
                      Certifications
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          )}

          {/* ── Idle ── */}
          {!loading && !result && !searched && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Award size={52} color="#27272a" style={{ marginBottom: 12 }} />
                <Typography variant="body2" sx={{ color: '#52525b' }}>
                  Enter a certificate ID above to verify its authenticity.
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      <Footer />
    </Box>
  );
};

export default CertificateVerify;
